import React, { useMemo, useEffect, useState, useContext } from 'react';
import classnames from 'classnames';
import Modal from 'react-modal';
import CloseIcon from '@wix/da-ds/pkg/Icons/24x24/Close';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { ScreenSizeContext } from '../../../Measured';
import { parse as parseSrcSet } from '../../../utils/srcset';
import imageStyles from './ImageFullview.scss';
function isBiggerThanViewport(width, height) {
    if (typeof window === 'undefined' || !width || !height) {
        return false;
    }
    return width > window.innerWidth || height > window.innerHeight;
}
var ZoomStage;
(function(ZoomStage) {
    ZoomStage[ZoomStage["INITIAL"] = 0] = "INITIAL";
    ZoomStage[ZoomStage["EXPANDED"] = 1] = "EXPANDED";
})(ZoomStage || (ZoomStage = {}));
export const ZoomModal = ({ src, srcSet, width, height, isPixel, onClose })=>{
    const [zoomStage, setZoomStage] = useState(0);
    const isMobile = useContext(MobileContext);
    const { width: screenWidth } = useContext(ScreenSizeContext);
    const largestImage = useMemo(()=>parseSrcSet(srcSet || '').concat({
            width,
            height,
            url: src
        }).filter((source)=>source.width).sort(({ width: a = 0 }, { width: b = 0 })=>a - b).pop(), [
        srcSet,
        width,
        height,
        src
    ]);
    const maxWidth = Math.max((largestImage === null || largestImage === void 0 ? void 0 : largestImage.width) || 0, screenWidth);
    const canZoom = isBiggerThanViewport(width, height);
    // close when escape is used
    useEffect(()=>{
        const onKeyDown = (e)=>{
            if (e.key === 'Escape') {
                onClose();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return ()=>window.removeEventListener('keydown', onKeyDown);
    }, [
        onClose
    ]);
    const toggleZoomStage = ()=>{
        if (canZoom) {
            // For mobile, we only want the initial zoom mode, then users can pinch+zoom all they want.
            if (zoomStage === 0 && !isMobile) {
                setZoomStage(1);
            } else {
                setZoomStage(0);
            }
        }
    };
    const isZoomed = canZoom && zoomStage === 1;
    // wrapping in a modal to avoid the can of worms with scrolling the underlying page and all
    // NoLeakyScroll only goes that far, and this seems more performant overall
    return /*#__PURE__*/ React.createElement(Modal, {
        className: classnames(imageStyles['zoom-content'], zoomStage === 1 && imageStyles['zoom-content-expanded']),
        isOpen: true,
        overlayClassName: imageStyles['zoomed'],
        onRequestClose: onClose
    }, /*#__PURE__*/ React.createElement("button", {
        className: imageStyles['unzoom'],
        onClick: onClose
    }, /*#__PURE__*/ React.createElement(CloseIcon, {
        size: IconSize.BIG,
        className: imageStyles['unzoom-icon']
    })), /*#__PURE__*/ React.createElement("div", {
        className: imageStyles['zoom-viewport']
    }, /*#__PURE__*/ React.createElement("img", {
        alt: "",
        className: classnames(imageStyles['image'], isPixel && imageStyles['pixel'], canZoom && zoomStage === 0 && imageStyles['initial-zoom'], canZoom && zoomStage === 1 && imageStyles['expanded-zoom']),
        src: isZoomed && (largestImage === null || largestImage === void 0 ? void 0 : largestImage.url) ? largestImage.url : src,
        style: isZoomed ? {
            maxWidth
        } : undefined,
        onClick: toggleZoomStage
    })));
};
ZoomModal.displayName = 'ZoomModal';
export default ZoomModal;
